"use client";

import React from "react";
import { motion } from "framer-motion";
import { House as Home, FileCheck, Landmark, Compass, Trees, CircleCheck as CheckCircle2, Building2, Sparkles, ArrowRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export default function DecaturHousingGuide() {
  return <section id="decaturhousingguide" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden border-b border-border/70">
      {/* Decorative architectural grid background texture */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 opacity-40 bg-[radial-gradient(hsl(var(--border))_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: 18
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="max-w-3xl mb-16 lg:mb-20">
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="outline" className="bg-card/70 border-border text-foreground px-3 py-1 text-xs font-medium tracking-wide uppercase">
              <Compass className="size-3.5 mr-1.5 text-ring" />
              Decatur Planning & Context Guide
            </Badge>
          </div>
          
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.15] mb-6">
            Understanding Decatur’s Architectural Fabric & Renovation Planning
          </h2>

          <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every residential renovation in Decatur takes place within a distinct architectural lineage and regulatory environment. From early 20th-century Craftsman bungalows in Oakhurst to mid-century brick ranches in Chelsea Heights and Winnona Park, successful home planning requires an understanding of structural nuances, municipal permitting boundaries, and neighborhood design guidelines.
          </p>
        </motion.div>

        {/* Editorial Content Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Card 1: Decatur Housing Stock */}
          <motion.div variants={itemVariants} className="lg:col-span-7">
            <Card className="h-full bg-card/90 backdrop-blur-md border-border shadow-xs hover:shadow-sm transition-shadow duration-300">
              <CardHeader className="border-b border-border/60 pb-5">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-md bg-secondary/80 flex items-center justify-center text-foreground border border-border">
                    <Home className="size-5 text-ring" />
                  </div>
                  <div>
                    <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block">
                      Architectural Typologies
                    </span>
                    <CardTitle className="font-serif text-2xl text-foreground mt-0.5">
                      Decatur’s Housing Stock & Core Remodeling Scope
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-5 text-sm sm:text-base leading-relaxed text-foreground/90 font-sans">
                <p>
                  Decatur’s residential neighborhoods are defined by a rich mixture of architectural eras, predominantly Craftsman bungalows constructed between 1910 and 1930, mid-century brick ranches built during the post-war building boom of the 1950s and 1960s, and classic American Foursquares and historic cottages.
                </p>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-secondary/40 border border-border/70 space-y-2">
                    <h3 className="font-semibold text-foreground flex items-center gap-2">
                      <span className="size-2 rounded-full bg-ring" />
                      Craftsman Bungalows & Historic Cottages
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Characterized by low-pitched gables, exposed rafter tails, deep covered front porches, and original heart pine subfloors. Common renovations focus on sensitively reorienting small, isolated rear kitchens into interconnected family spaces, expanding small primary baths, leveling settlement in pier-and-beam foundations, and executing second-story dormer or rear additions that preserve street-facing scale.
                    </p>
                  </div>

                  <div className="p-4 rounded-lg bg-secondary/40 border border-border/70 space-y-2">
                    <h4 className="font-semibold text-foreground flex items-center gap-2">
                      <span className="size-2 rounded-full bg-ring" />
                      Mid-Century Brick Ranches
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Featuring low-slope rooflines, linear footprints, and crawlspace or slab foundations. Renovation programs frequently involve removing interior load-bearing masonry or timber walls to establish open living-dining-kitchen hubs, converting unfinished daylight basements into livable suites, upgrading original plumbing systems, and modernizing fenestration with energy-efficient window assemblies.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Card 2: Permitting Realities */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <Card className="h-full bg-card/90 backdrop-blur-md border-border shadow-xs hover:shadow-sm transition-shadow duration-300">
              <CardHeader className="border-b border-border/60 pb-5">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-md bg-secondary/80 flex items-center justify-center text-foreground border border-border">
                    <FileCheck className="size-5 text-ring" />
                  </div>
                  <div>
                    <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block">
                      Jurisdictional Framework
                    </span>
                    <CardTitle className="font-serif text-2xl text-foreground mt-0.5">
                      City of Decatur vs. DeKalb County
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-4 text-sm sm:text-base leading-relaxed text-foreground/90 font-sans">
                <p>
                  A critical early step in planning any structural renovation is establishing whether a property lies within the 4.4-square-mile incorporated boundary of the <strong>City of Decatur</strong> or within <strong>Unincorporated DeKalb County</strong> (e.g., postal addresses in 30030 or 30033).
                </p>
                <ul className="space-y-3 pt-1">
                  <li className="flex items-start gap-2.5 text-sm">
                    <CheckCircle2 className="size-4 text-ring shrink-0 mt-1" />
                    <span><strong>Tree Canopy Protection:</strong> The City of Decatur maintains a rigorous Tree Canopy Conservation Ordinance requiring tree impact plans and recompense calculations for site disturbances.</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm">
                    <CheckCircle2 className="size-4 text-ring shrink-0 mt-1" />
                    <span><strong>Impervious Surface Maximums:</strong> City zoning enforces strict lot coverage and stormwater run-off thresholds, directly shaping the footprint of rear additions, patios, and driveways.</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm">
                    <CheckCircle2 className="size-4 text-ring shrink-0 mt-1" />
                    <span><strong>Plan Review & Field Inspections:</strong> City of Decatur projects undergo independent municipal plan review at One Decatur Plaza, distinct from DeKalb County’s Central Permitting division.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </motion.div>

          {/* Card 3: Historic District Guidelines */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <Card className="h-full bg-card/90 backdrop-blur-md border-border shadow-xs hover:shadow-sm transition-shadow duration-300">
              <CardHeader className="border-b border-border/60 pb-5">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-md bg-secondary/80 flex items-center justify-center text-foreground border border-border">
                    <Landmark className="size-5 text-ring" />
                  </div>
                  <div>
                    <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block">
                      Preservation & Standards
                    </span>
                    <CardTitle className="font-serif text-2xl text-foreground mt-0.5">
                      Historic District Design Review
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-4 text-sm sm:text-base leading-relaxed text-foreground/90 font-sans">
                <p>
                  Homes located within Decatur’s designated local historic districts—such as the <em>McDonough-Adams-Kings Highway (MAK) Historic District</em>, <em>Clairemont Avenue</em>, or <em>Old Decatur</em>—are subject to design review by the Decatur Historic Preservation Commission (HPC).
                </p>
                <div className="p-4 rounded-lg bg-secondary/40 border border-border/70 space-y-2 text-sm text-muted-foreground">
                  <p className="font-semibold text-foreground">Certificate of Appropriateness (COA):</p>
                  <p>
                    Required for any exterior work visible from the public right-of-way prior to building permit issuance. Pure interior renovations do not require HPC review, but window replacements, exterior cladding alterations, porch modifications, roofline changes, and street-visible additions must conform to the Secretary of the Interior’s Standards for Rehabilitation.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Card 4: Homeowner Catalysts & Triggers */}
          <motion.div variants={itemVariants} className="lg:col-span-7">
            <Card className="h-full bg-card/90 backdrop-blur-md border-border shadow-xs hover:shadow-sm transition-shadow duration-300">
              <CardHeader className="border-b border-border/60 pb-5">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-md bg-secondary/80 flex items-center justify-center text-foreground border border-border">
                    <Sparkles className="size-5 text-ring" />
                  </div>
                  <div>
                    <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground block">
                      Planning Triggers
                    </span>
                    <CardTitle className="font-serif text-2xl text-foreground mt-0.5">
                      Common Catalysts: Remodeling vs. Relocating
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-4 text-sm sm:text-base leading-relaxed text-foreground/90 font-sans">
                <p>
                  For many Decatur homeowners, major renovation planning is initiated by specific spatial milestones rather than purely cosmetic updates:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                  <div className="p-3.5 rounded-md border border-border/80 bg-background/50">
                    <h5 className="font-semibold text-sm text-foreground mb-1">Spatial Expansion</h5>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
                      Growing households needing an additional bedroom, dedicated home office, or a dedicated main-level owner suite with walk-in storage.
                    </p>
                  </div>
                  <div className="p-3.5 rounded-md border border-border/80 bg-background/50">
                    <h5 className="font-semibold text-sm text-foreground mb-1">Kitchen Reconfigurations</h5>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
                      Replacing segmented, dark galley configurations with central islands, improved prep flow, pantry capacity, and daylight exposure.
                    </p>
                  </div>
                  <div className="p-3.5 rounded-md border border-border/80 bg-background/50">
                    <h5 className="font-semibold text-sm text-foreground mb-1">Bathroom Modernization</h5>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
                      Updating vintage 5x8 hall baths with walk-in showers, double vanities, functional ventilation, and modern plumbing stacks.
                    </p>
                  </div>
                  <div className="p-3.5 rounded-md border border-border/80 bg-background/50">
                    <h5 className="font-semibold text-sm text-foreground mb-1">Staying in the Neighborhood</h5>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
                      Balancing school zoning, walkable downtown access, and existing mortgage terms against the transaction costs and competition of moving.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        {/* Section Footer Context Line */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.2
      }} className="mt-14 pt-8 border-t border-border/80 flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-sm text-muted-foreground font-sans text-center sm:text-left">
            Explore detailed specifications, budget ranges, and project scopes for Decatur residences in our planning guides.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" asChild size="sm">
              <Link to="/DecaturRemodelingPlanningGuidePage">
                Planning Guide
              </Link>
            </Button>
            <Button variant="secondary" asChild size="sm">
              <Link to="/DecaturRemodelingCostGuidePage" className="flex items-center gap-1.5">
                Cost Guide
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}